/*
  <dv-carousel> — horizontal product/media rail with drag-to-scroll, stepped
  nudge buttons and a scroll-progress rail.

  A carousel may hold several `[data-dv-track]` panels; exactly one is visible
  at a time and the optional `[role="tab"]` row switches between them. Keeping
  the tabs inside the element lets one set of nudge buttons and one progress
  rail serve every panel, which is what the design asks for.

  Expected markup:
    <dv-carousel>
      <div class="dv-nudge">
        <button data-dv-nudge="prev">…</button>
        <button data-dv-nudge="next">…</button>
      </div>
      <div class="dv-track" data-dv-track>…cards…</div>
      <div class="dv-progress"><span data-dv-progress></span></div>
    </dv-carousel>
*/

const DV_TRACK_GAP = 20;
const DV_DRAG_THRESHOLD = 4;

class DvCarousel extends HTMLElement {
  connectedCallback() {
    this.tracks = Array.from(this.querySelectorAll('[data-dv-track]'));
    if (!this.tracks.length) return;

    this.bar = this.querySelector('[data-dv-progress]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

    this.tracks.forEach((track) => {
      track.addEventListener('scroll', () => this.updateProgress(), {passive: true});
      this.enableDrag(track);
    });

    this.querySelectorAll('[data-dv-nudge]').forEach((button) => {
      button.addEventListener('click', () => this.nudge(button.dataset.dvNudge));
    });

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onTabKey(event, index));
    });

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);

    // Late-decoding images can widen the track after first layout.
    window.addEventListener('load', () => this.measure(), {once: true});
    this.measure();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get track() {
    return this.tracks.find((track) => !track.hidden) || this.tracks[0];
  }

  measure() {
    const track = this.track;
    if (!track) return;
    this.toggleAttribute('data-scrollable', track.scrollWidth - track.clientWidth > 1);
    this.updateProgress();
  }

  updateProgress() {
    if (!this.bar) return;
    const track = this.track;
    const span = track.scrollWidth - track.clientWidth;
    const pct = span > 0 ? track.scrollLeft / span : 0;
    this.bar.style.width = `${Math.max(8, pct * 100)}%`;
  }

  nudge(direction) {
    const track = this.track;
    const card = track.firstElementChild;
    const step = card ? card.offsetWidth + DV_TRACK_GAP : 340;
    track.scrollBy({left: direction === 'next' ? step : -step, behavior: 'smooth'});
  }

  select(index) {
    this.tabs.forEach((tab, position) => {
      const active = position === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.tracks.forEach((track, position) => {
      track.hidden = position !== index;
    });
    // Land at the start of the row whenever the category changes.
    this.track.scrollLeft = 0;
    this.measure();
  }

  onTabKey(event, index) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const count = this.tabs.length;
    const next = event.key === 'ArrowRight' ? (index + 1) % count : (index - 1 + count) % count;
    this.select(next);
    this.tabs[next].focus();
  }

  /*
    Pointer dragging for mouse users only — touch already scrolls natively, and
    hijacking it costs the momentum fling. A drag past the threshold swallows
    the click so the card link underneath does not navigate.
  */
  enableDrag(track) {
    let dragging = false;
    let moved = false;
    let originX = 0;
    let originLeft = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragging = true;
      moved = false;
      originX = event.clientX;
      originLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - originX;
      if (Math.abs(dx) > DV_DRAG_THRESHOLD) {
        moved = true;
        track.setPointerCapture(event.pointerId);
      }
      track.scrollLeft = originLeft - dx;
    });

    const release = (event) => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    };

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );
  }
}

if (!customElements.get('dv-carousel')) customElements.define('dv-carousel', DvCarousel);

/*
  Theme editor: only one tab panel is in the DOM-visible state at a time, so
  selecting a tab block in the sidebar has to switch to it — otherwise the
  merchant edits a panel they cannot see.
*/
document.addEventListener('shopify:block:select', (event) => {
  const tab = event.target.closest?.('[role="tab"]');
  const carousel = tab?.closest('dv-carousel');
  if (!carousel?.tabs) return;
  const index = carousel.tabs.indexOf(tab);
  if (index >= 0) carousel.select(index);
});
